import { useState, useMemo, useRef, useEffect } from "react";
import {
  Search,
  ChevronDown,
  X,
  Download,
  Upload,
  Pencil,
  Trash2,
} from "lucide-react";
import { I, SK, fc, fd, fmin, nid, pname } from "../utils";
import {
  MyAllToggle,
  SegmentedControl,
  LockButton,
  AddButton,
  ModalLayout,
} from "../components/SharedUI";
import CaseTimeEntriesSection from "../components/CaseTimeEntriesSection";
import InvoiceTable from "../components/InvoiceTable"; // 158 §5 — the one table
import { isOverduePF as isOverduePFShared } from "../lib/clientMatters.mjs"; // 191 E.2f — one overdue
// 144 §5 — this view had no confirm/undo surface (its single-row delete used
// window.confirm); bulk delete needs the shared single-slot hook + its two
// surfaces, mounted here exactly as CaseView mounts them.
import { useConfirmAndUndo, ConfirmDeleteDialog, UndoToast } from "../components/useConfirmAndUndo";
import { api } from "../api";
import type { TeBulkUpdateResult } from "../api"; // 145 §1 — the bulk-edit result the table hands back
import {
  matterPassesProspect,
  prospectFilterOptions,
  type ProspectFilterValue,
  type ProspectTaggable,
} from "../lib/prospectFilter"; // 139 §3

/* 139 §3 (rule 4) — the two row shapes the prospect arm reads, written out
   rather than taken as `any`. Their neighbours in this file are `any` and stay
   that way (rule 5: do not rename or retype existing code in passing). */
type ProspectMatterRow = ProspectTaggable & { id: number | string };
type InvoiceMatterRef = {
  caseId?: number | string | null;
  ccId?: number | string | null;
};
import { useRoleLabels } from "../hooks/useRoleLabels";
import CountersStrip, { stripFigures, useEntryFees } from "../components/CountersStrip"; // 190 round 6 [W] — the FIRM host
import type { TFn } from "../lib/taxonomyTypes";
import { usePermissions } from "../hooks/usePermissions";
import { getStatusBadgeTooltip } from "../lib/invoiceTransitions";
import { useTranslation } from "../i18n";
import { RAD, FS, Z } from "../lib/tokens";
import Table from "../components/Table";

// Payment-status display config now lives in the invoice_payment_status
// taxonomy (M+3.6 C4). The helper normalises the legacy "partially paid"
// (with space) to canonical "partially_paid" before lookup.

const scopeKey = (s: any) => (s ? `${s.type}:${s.id}` : "all");
const sameScope = (a: any, b: any) =>
  (!a && !b) || (a && b && a.type === b.type && a.id === b.id);

const InvoiceView = ({
  inv,
  sInv,
  ca,
  cc,
  cl,
  co,
  te,
  sTe,
  tkTypes,
  currentUser,
  s,
  toast,
  nav,
  om,
  lastCaseCtx,
  onViewInvoice,
  onCreateProForma,
  // 150 §3 — the firm's week for the block's period filter; this host holds
  // the settings object, and periodBounds' resolver accepts either shape.
  firmSettings,
}: any) => {
  const { t } = useTranslation();
  // Scope: null = All Cases; else {type:"ca"|"cc", id}
  const [scope, setScope] = useState(() => lastCaseCtx || null);
  console.log("InvoiceView render:", {
    scope,
    scopeType: scope?.type,
    scopeId: scope?.id,
    hasTE: !!te,
    teLen: te?.length,
  });
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeQ, setScopeQ] = useState("");
  const scopeRef = useRef<any>(null);

  const [q, setQ] = useState("");
  const [statusFilt, setStatusFilt] = useState("all");
  // 179 C.3 — the "Invoiced (Billit push)" filter axis STOOD HERE: it split the
  // book on billitSubmittedAt, a stamp no live path ever wrote.
  /* 139 §3 — the prospect axis, third in this toolbar's row of filters.
     Default "both", so an untouched Invoicing screen lists the same book. */
  const [invProspect, setInvProspect] = useState<ProspectFilterValue>("both");
  const [my, setMy] = useState(false);
  // Phase 35 (C1 / D1) — the "formal" type axis is gone (pro formas only); the
  // old typeFilt state is removed. statusFilt now drives the pro-forma lifecycle
  // filter (All/Unsent/Sent/Paid/Unpaid/Overdue) defined in the filter block.
  // 153 §2 — the Gate 4 C4 trash filter is GONE, not disabled. Phase 35
  // Stage-3 r2 removed the Live/Trash tabs (the comment further down in the
  // filter bar still says so) and left `trashFilter` frozen at "live", so the
  // whole trash half of this view — its fetch, its list, its Restore button —
  // was unreachable code pretending to be a second tab.
  /* 160 §5 — `teSel` STOOD HERE AND IT IS GONE, with the two functions that
     were its only readers. `setTeSel` was never called to SELECT anything —
     only to clear, here on scope change and at the end of each of those two —
     so the set was empty at every moment of its life and both functions began
     `if (!teSel.size) return`. The selection this screen actually acts on
     belongs to TimeEntryTable, which owns its own. */
  const [doubles, setDoubles] = useState<any>(null);

  useEffect(() => {
    if (!scopeOpen) return;
    const h = (e: any) => {
      if (scopeRef.current && !scopeRef.current.contains(e.target)) {
        setScopeOpen(false);
        setScopeQ("");
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [scopeOpen]);

  const allInv = inv || [];
  const allTe = te || [];

  // Scope resolution
  const scopedCase = useMemo(() => {
    if (!scope) return null;
    if (scope.type === "ca")
      return (ca || []).find((c: any) => c.id === scope.id) || null;
    if (scope.type === "cc")
      return (cc || []).find((c: any) => c.id === scope.id) || null;
    return null;
  }, [scope, ca, cc]);

  // My = cases where current user is responsible partner
  const myCaseIds = new Set([
    ...(ca || [])
      .filter((c: any) => (c.responsiblePartners || []).includes(currentUser))
      .map((c: any) => c.id),
    ...(cc || [])
      .filter((c: any) => (c.responsiblePartners || []).includes(currentUser))
      .map((c: any) => c.id),
  ]);

  /* 139 §3 — AN INVOICE INHERITS ITS MATTER'S TAG. The rows on this screen are
     invoices, not matters, so the axis reaches them through the matter each one
     belongs to. Two id spaces, kept apart: `caseId` indexes Case, `ccId`
     indexes CollectiveCase, and the two number independently (the ccMetrics
     id-collision family is what a shared set would re-open).

     THE TEST IS "IS IT A PROSPECT'S", NOT "IS ITS MATTER PRESENT", and the
     asymmetry is deliberate. "prospects" is a positive claim: the invoice's
     matter must resolve INTO the tagged set. "regular" is everything not known
     to be a prospect's — so an invoice whose matter is missing from the
     viewer's arrays, or that hangs off no matter at all, stays in the firm's
     ordinary book rather than vanishing from both halves of a partition.
     Resolution failure must not delete money from a total. */
  const _prospectCaIds = new Set(
    (ca || [])
      .filter((c: ProspectMatterRow) => !!c.isProspect)
      .map((c: ProspectMatterRow) => c.id),
  );
  const _prospectCcIds = new Set(
    (cc || [])
      .filter((c: ProspectMatterRow) => !!c.isProspect)
      .map((c: ProspectMatterRow) => c.id),
  );
  const invIsProspect = (i: InvoiceMatterRef) =>
    (i.caseId != null && _prospectCaIds.has(i.caseId)) ||
    (i.ccId != null && _prospectCcIds.has(i.ccId));

  // Phase 35 Stage-3 — Overdue = unpaid-ish AND past due date, no grace. 191
  // E.2f: the ONE definition (src/lib/clientMatters.mjs), handed this render's
  // clock once, as this view's own copy took it.
  const todayStr = new Date().toISOString().slice(0, 10);
  const isOverduePF = (i: any) => isOverduePFShared(i, todayStr);

  // Filter invoices (global or scoped)
  let filtered = allInv;
  if (scope) {
    filtered = filtered.filter(
      (i: any) =>
        (scope.type === "ca" && i.caseId === scope.id) ||
        (scope.type === "cc" && i.ccId === scope.id),
    );
  }
  if (my && currentUser && !scope) {
    filtered = filtered.filter(
      (i: any) =>
        myCaseIds.has(i.caseId) ||
        myCaseIds.has(i.ccId) ||
        i.createdBy === currentUser,
    );
  }
  /* 139 §3 — the prospect arm, beside the scope and my arms it copies. The
     shared predicate, applied to the tag the invoice inherits above, so this
     screen and the two matter lists partition by exactly one rule. */
  filtered = filtered.filter((i: InvoiceMatterRef) =>
    matterPassesProspect({ isProspect: invIsProspect(i) }, invProspect),
  );
  // Phase 35 Stage-3 — pro-forma lifecycle filter: All / Unpaid / Paid /
  // Overdue. (Unsent/Sent dropped — no draft/ready/sent states.)
  if (statusFilt === "paid")
    filtered = filtered.filter((i: any) => i.paymentStatus === "paid");
  else if (statusFilt === "unpaid")
    filtered = filtered.filter((i: any) =>
      ["unpaid", "partially_paid"].includes(i.paymentStatus),
    );
  else if (statusFilt === "overdue") filtered = filtered.filter(isOverduePF);

  if (q) {
    const ql = q.toLowerCase();
    filtered = filtered.filter(
      (i: any) =>
        (i.number || "").toLowerCase().includes(ql) ||
        (i.caseRef || "").toLowerCase().includes(ql) ||
        (i.caseTitle || "").toLowerCase().includes(ql) ||
        (i.clientName || "").toLowerCase().includes(ql),
    );
  }


  // Time entries scoped to selected case
  const scopedTe = useMemo(() => {
    if (!scope) return [];
    return allTe.filter((t: any) =>
      scope.type === "ca" ? t.caId === scope.id : t.ccId === scope.id,
    );
  }, [allTe, scope]);

  /* 148 §1.2 — `teVisible` STOOD HERE AND IT IS GONE. It was `scopedTe` under
     the frozen `teBilFilt` (§1.1), and the brief that ruled on it believed it
     had no reader at all — because this view's table mount is fed `scopedTe`,
     unfiltered, which is why Invoicing was the one surface reading the true
     counts. It had two, and both were checked before it was deleted:

       · the EXPORT (:below) — LIVE, and lying in exactly the way §1.2 names.
         It now exports `scopedTe`.
       · `openProForma`'s `inView` gate — DEAD, and dead twice over: that
         function has NO CALLER in this file, and its own `!teSel.size` guard
         could never pass anyway, since `setTeSel` is only ever called to CLEAR
         (on scope change). It was repointed at `scopedTe` and left otherwise
         alone, and the three were named here "so the next brief can" remove
         them.

     160 §5 IS THAT BRIEF: `openProForma`, `markBilled` and `teSel` ARE GONE.
     What survives of 148 §1.2 is the export, which was the live half. */
  // Phase M+2b: time-entries admin affordances on the invoice view (Pro
  // Forma button etc.). Original semantic was admin-only; per-case RP
  // overlay would need a per-invoice caseId resolution. Kept admin-only.
  const _perms = usePermissions();
  const isAdminTe = !!_perms.viewer?.isAdmin;
  const { confirmAndUndo, confirmDlg, cancelConfirm, undoToast, runUndo } =
    useConfirmAndUndo();

  // Phase 35 Stage-3 r2 (§4) — the summary cards are RP/Admin-only. Reuses the
  // existing RP determination (responsiblePartners ∋ currentUser, same predicate
  // as the My/All filter) plus the isAdmin overlay. Admin aggregates the whole
  // firm; an RP aggregates only the matters they are responsible partner on; a
  // plain collaborator sees no cards at all. Separate ca/cc sets avoid id
  // collisions; memoised so they're stable in markFeeSums' deps.
  const myRpCaIds = useMemo(
    () =>
      new Set(
        (ca || [])
          .filter((c: any) => (c.responsiblePartners || []).includes(currentUser))
          .map((c: any) => c.id),
      ),
    [ca, currentUser],
  );
  const myRpCcIds = useMemo(
    () =>
      new Set(
        (cc || [])
          .filter((c: any) => (c.responsiblePartners || []).includes(currentUser))
          .map((c: any) => c.id),
      ),
    [cc, currentUser],
  );
  // Block visibility: admin always; scoped → RP of THIS matter; All-Cases → RP
  // of at least one matter. Since 150 §4 this gates the BLOCK's counter row
  // (canSeeCounters) — the card row this view carried is retired there.
  const canSeeCards =
    isAdminTe ||
    (scope
      ? scope.type === "ca"
        ? myRpCaIds.has(scope.id)
        : myRpCcIds.has(scope.id)
      : myRpCaIds.size > 0 || myRpCcIds.size > 0);
  // 144 §4/§5 — the bulk-edit hint on the invoice mount, the canMarkBilled
  // shape scoped to THIS matter (the time-entry table here renders only under
  // a scope): admin, or holding the bulkEdit bit (role default or live grant)
  // while RP of the scoped matter. A UI hint; the route re-judges per row.
  const canBulkEditTe =
    !!scope &&
    (isAdminTe ||
      (_perms.can(
        "caseView.timeEntries.bulkEdit",
        scope.type === "cc" ? { ccId: scope.id } : { caseId: scope.id },
      ) &&
        (scope.type === "cc" ? myRpCcIds.has(scope.id) : myRpCaIds.has(scope.id))));
  const canBulkDeleteTe =
    !!scope &&
    (isAdminTe ||
      (_perms.can(
        "caseView.timeEntries.bulkDelete",
        scope.type === "cc" ? { ccId: scope.id } : { caseId: scope.id },
      ) &&
        (scope.type === "cc" ? myRpCcIds.has(scope.id) : myRpCaIds.has(scope.id))));

  // Summaries (context-aware). Phase 35 Stage-3 r2 (§9) — the per-invoice
  // fee/total/paid/outstanding sums were retired together with the old Saldo /
  // Fees-logged / Billed cards. 158 §5 — sumCredits went with the payment
  // modal, its only consumer, into components/InvoiceTable.tsx.
  /* 150 §4 — THE CARD ROW AND ITS MACHINERY RETIRED INTO THE BLOCK. The
     invFees POST (markFeeSums), pfActive and the pfTotal* trio lived here for
     the seven summary cards; the block's counter row is their generalisation
     — five row-based cards over the FILTERED rows (server-handed per-row
     amounts; the §4.3 cent proof is in the block commit), Envoyé row-exact
     off the frozen invoicedAmount, Payé/Dû invoice-level over the invoices
     the filtered rows sit on. Two deliberate narrowings travel with the move,
     stated rather than discovered: the cards now render only where the te
     section renders (a scope chosen — the UNSCOPED firm-wide sums live on the
     Dashboard's block, the spans-matters surface, behind the same admin/RP
     gate); and Payé/Dû cover the invoices CONCERNED by the filtered rows, not
     every pro forma in the list scope. useRoleLabels stays — the block takes
     _roleRows. */
  const { rows: _roleRows } = useRoleLabels();
  /* ── 190 ROUND 6 [W] — THE FIRM HOST, RULED: at "Tous les dossiers" the
     seven counters are THE FIRM, and AT ALL TIMES UNFILTERED — "that's the
     firm overview". The list's search and status filters do not touch it,
     and neither does the reader's own time filter (there is none here). THE
     OMISSION HE FOUND: this view gated the whole time-entries section on
     `scope && scopedCase`, so at firm scope there was no section and no
     strip — the giant list scrolled with nothing pinned. The strip is
     CountersStrip.tsx (one component, three hosts); this is its third
     declaration: every row the viewer holds (`te` — the server scrubs what
     they may not read), every matter of both lists (`ca` + `cc`, their
     served `recap`). The per-row money is the same POST the matter host
     makes (useEntryFees), over the firm's rows, resolved to their matters. */
  const firmHost = !(scope && scopedCase) && canSeeCards;
  // the rows this view holds are `any`-typed props; read ONCE into typed locals (185's method) —
  // memoised on the prop identities, so the hooks below key on them and not on a fresh `?? []`
  const firmCa = useMemo<readonly { id: number }[]>(() => (ca as { id: number }[] | null | undefined) ?? [], [ca]);
  const firmCc = useMemo<readonly { id: number }[]>(() => (cc as { id: number }[] | null | undefined) ?? [], [cc]);
  const firmCo = useMemo<readonly unknown[]>(() => (co as unknown[] | null | undefined) ?? [], [co]);
  const firmMatterOf = useMemo(() => {
    const byCa = new Map<number, unknown>(firmCa.map((c) => [c.id, c]));
    const byCc = new Map<number, unknown>(firmCc.map((c) => [c.id, c]));
    return (e: unknown): unknown => {
      const r = e as { caId?: number | null; ccId?: number | null };
      if (r.ccId != null) return byCc.get(r.ccId) ?? null;
      if (r.caId != null) return byCa.get(r.caId) ?? null;
      return null;
    };
  }, [firmCa, firmCc]);
  const firmRows = useMemo<readonly unknown[]>(() => (firmHost ? ((te as unknown[] | null | undefined) ?? []) : []), [firmHost, te]);
  const firmFees = useEntryFees({ entries: firmRows, caseOf: firmMatterOf, co: firmCo, roleRows: _roleRows });
  const firmFigures = useMemo(
    () => (firmHost ? stripFigures(firmRows as Parameters<typeof stripFigures>[0], firmFees, [...firmCa, ...firmCc]) : null),
    [firmHost, firmRows, firmFees, firmCa, firmCc],
  );

  const scopedInv = scope
    ? allInv.filter((i: { caseId?: number | null; ccId?: number | null }) =>
        scope.type === "ca" ? i.caseId === scope.id : i.ccId === scope.id,
      )
    : allInv;
  // Phase 35 Stage-3 — pill counts for the All / Unpaid / Paid / Overdue filter.
  const counts = {
    all: scopedInv.length,
    unpaid: scopedInv.filter((i: any) =>
      ["unpaid", "partially_paid"].includes(i.paymentStatus),
    ).length,
    paid: scopedInv.filter((i: any) => i.paymentStatus === "paid").length,
    overdue: scopedInv.filter(isOverduePF).length,
    cancelled: scopedInv.filter((i: any) => i.paymentStatus === "cancelled").length,
  };

  // createFormal removed in Phase M+3 — see scope §6.1 (route retired).

  // Phase 35 Stage-3 — the credit feature is removed (replaced by the
  // commercial-deduction field in the create modal).

  /* 160 §5 — `markBilled` and `openProForma` STOOD HERE. 148 §1.2 found them
     dead, said so in the comment above, and left them because it had no ruling
     to remove them; this section is that ruling. Neither had a caller in this
     file, and neither could have done anything if it had: both opened on
     `!teSel.size`, and nothing ever put anything in that set. The live pro
     forma path is TimeEntryTable's own `onProForma` (:below), which acts on
     the table's selection and reaches the same `onCreateProForma`. */

  // Phase 34 (§6) — surface "doubles": the same email/document recorded more than
  // once. In-case is client-side over the loaded rows (group by source identity);
  // cross-case hits the server query. Mark-agnostic (joins on egid / docItemId).
  const handleDoubles = async (mode: any) => {
    if (!scope) return;
    if (mode === "in-case") {
      const byKey: Record<string, any> = {};
      for (const e of scopedTe) {
        const k = e.egid ? "e:" + e.egid : e.docItemId ? "d:" + e.docItemId : null;
        if (!k) continue;
        (byKey[k] = byKey[k] || []).push(e);
      }
      setDoubles({ mode, rows: Object.values(byKey).filter((g) => g.length > 1).flat() });
    } else {
      try {
        const q = scope.type === "cc" ? "ccId=" + scope.id : "caseId=" + scope.id;
        const r = (await api.get("/time-entries/doubles?" + q)) as { doubles?: unknown[] } | null;
        setDoubles({ mode, rows: (r && r.doubles) || [] });
      } catch (e2) {
        toast(t("te.doubles.failed", { fallback: "Could not load doubles" }), "error");
      }
    }
  };


  /* Scope dropdown options.
     139 §3 — the dropdown follows the axis, because it offers the matters this
     screen is currently ABOUT. It is the one lookup-shaped thing here that is
     population rather than resolution: `scopedCase` above still resolves a
     chosen scope off the whole `ca`/`cc`, so a scope that stops passing the
     axis keeps its name in the header and simply lists no rows — the same
     thing any other filter combination on this screen does. */
  const scopeOptions = useMemo(() => {
    const caOpts = (ca || [])
      .filter((c: ProspectTaggable) => matterPassesProspect(c, invProspect))
      .map((c: any) => ({
        type: "ca",
        id: c.id,
        ref: c.ref || "",
        title: c.title || "",
      }));
    const ccOpts = (cc || [])
      .filter((c: ProspectTaggable) => matterPassesProspect(c, invProspect))
      .map((c: any) => ({
        type: "cc",
        id: c.id,
        ref: c.ref || "",
        title: c.title || "",
      }));
    return [...caOpts, ...ccOpts].sort((a, b) =>
      (a.ref || "").localeCompare(b.ref || ""),
    );
  }, [ca, cc, invProspect]);
  const scopeFiltered = scopeQ
    ? scopeOptions.filter((o) =>
        (o.ref + " " + o.title).toLowerCase().includes(scopeQ.toLowerCase()),
      )
    : scopeOptions;

  const scopeLabel = !scope
    ? t("invoice.scope.all_cases")
    : scopedCase
      ? `${scopedCase.ref || "—"} — ${scopedCase.title || ""}${scope.type === "cc" ? " (" + t("invoice.scope.collective") + ")" : ""}`
      : t("invoice.scope.not_found");

  /* 214 addendum 3 §23 [RULED 61] — `teStatusPill` stood here (52 lines) and was CALLED NOWHERE: the Invoices
     screen's time table is CaseTimeEntriesSection (below), whose pill is TimeEntryTable's TeMarkPill — « Facturé »
     for a row in a pro forma, else the mark's word (« Facturable » for a billable row awaiting one). The dead
     function read « Facturé » off the mark's mirror `billed`; deleted rather than mounted. Its three keys
     (`invoiceTable.billed`, `invoiceTable.invoiced`, `invoice.te.unbilled`) are retired, said in the note. */

  /* 151C §2 (iii) — the pro forma list, hoisted so it can render in TWO
     places without a second copy: standalone where no matter is scoped
     (unchanged), or handed to the time-entry block as afterTable when one
     is — inside the scroll window, following the entries (reaching it =
     scroll to bottom; accepted by the ruling). */
  const invListBlock = (
    /* 193 §D — `.invBlock` is the SECTION the pro forma window measures
       itself against (hooks/useWindowMax.ts, `sectionSelector`): the controls
       row above the table is its overhead, so — the te pattern — the row
       stands still while the pro formas scroll under the pinned header. One
       wrapper, no style. */
    <div className="invBlock">
      {/* Invoice list controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          marginTop: scope ? 8 : 0,
          flexWrap: "wrap",
        }}
      >
        {scope && (
          <h3 style={{ fontSize: FS.s12, margin: 0, color: "var(--tp)" }}>
            {t("invoice.list.title")}
          </h3>
        )}
        <div className="search-box" style={{ width: 220 }}>
          <Search size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
          <input
            placeholder={t("invoice.list.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {!scope && currentUser && (
          <MyAllToggle
            {...({} as any)}
            my={my}
            onMy={() => setMy(true)}
            onAll={() => setMy(false)}
            counts={{
              my: allInv.filter(
                (i: any) =>
                  myCaseIds.has(i.caseId) ||
                  myCaseIds.has(i.ccId) ||
                  i.createdBy === currentUser,
              ).length,
              all: allInv.length,
            }}
          />
        )}
        {/* Phase 35 (C1 / D1+D8) — pro-forma lifecycle filter. Replaces the
          dropped type filter (proforma/formal) and the raw payment-status
          filter with the derived set All/Unsent/Sent/Paid/Unpaid/Overdue. */}
        <SegmentedControl
          {...({} as any)}
          value={statusFilt}
          options={[
            ["all", t("common.all")],
            ["unpaid", t("invoice.pf.unpaid", { fallback: "Unpaid" })],
            ["paid", t("invoice.pf.paid", { fallback: "Paid" })],
            ["overdue", t("invoice.pf.overdue", { fallback: "Overdue" })],
          ]}
          onChange={setStatusFilt}
          counts={counts}
        />
        {/* 139 §3 — the prospect axis, third PeriodFilter in a row of them:
            this screen's own recipe, as the pill row was Costs & Fees's. The
            counts are over the SCOPED invoice set the two filters beside it
            count (`counts.all` is `scopedInv.length`), so all three pills agree
            on the population they describe. */}
        <SegmentedControl
          value={invProspect}
          options={prospectFilterOptions(t)}
          onChange={setInvProspect}
          counts={{
            both: scopedInv.length,
            prospects: scopedInv.filter((i: InvoiceMatterRef) => invIsProspect(i))
              .length,
            regular: scopedInv.filter((i: InvoiceMatterRef) => !invIsProspect(i))
              .length,
          }}
        />
        {/* Phase 35 Stage-3 r2 (§2) — the Live/Trash tabs are removed: pro forma
            Delete is now a permanent hard delete (no trash bucket).
            160 §3 — AND THE COUNTER THAT FOLLOWED THEM IS GONE TOO. It printed
            "N pro forma invoices" at the end of a row of three PeriodFilters
            whose pills already carry every count it repeated — `counts.all` IS
            the scoped population, and each pill states its own slice. A number
            stated twice invites the reading that they are two different
            numbers. The key `invoice.list.count` (and its `_one` plural) had no
            other reader and left the seed with it. */}
      </div>

      {/* 158 §5 — ONE INVOICE TABLE. This table used to be written out here
          and a SECOND, older one lived in `components/InvoiceTable.tsx` for
          CV and CCV — where it showed a status WORD ("Partiel") in place of
          the two figures a firm reads, which is how the matter recap came to
          contradict this screen. The whole table moved into that file, verbatim
          and with its sort state and payment modal; this view now mounts it
          like the other two, and hands it the only two things that are its
          own: the filtered rows and the tail of its empty line. */}
      <InvoiceTable
        invoices={filtered}
        scope={scope}
        emptyHint={
          <>
            {statusFilt !== "all" ? t("invoice.list.empty_status_suffix") : ""}
            {q ? t("invoice.list.empty_q_suffix", { q }) : ""}
          </>
        }
        inv={inv}
        sInv={sInv}
        toast={toast}
        onViewInvoice={onViewInvoice}
        /* 179 B.6 — the approval pill's switch. Typed at the seam so the
           host's `any` does not add an unsafe assignment (the eslint ratchet). */
        /* 193 §D — this screen's list is a window with a pinned header; the
           matter views keep the plain table. */
        windowed
      />
    </div>
  );

  return (
    <>
      {/* Scope selector */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <div ref={scopeRef} style={{ position: "relative", minWidth: 320 }}>
          <button
            type="button"
            onClick={() => setScopeOpen((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              width: "100%",
              background: "var(--bc)",
              color: "var(--tp)",
              border: "1px solid var(--br)",
              borderRadius: RAD.r6,
              fontSize: FS.s11,
              fontFamily: "inherit",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span
              style={{
                fontSize: FS.s9,
                color: "var(--tm)",
                textTransform: "uppercase",
                letterSpacing: ".4px",
              }}
            >
              {t("invoice.scope")}
            </span>
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: scope ? 600 : 400,
              }}
            >
              {scopeLabel}
            </span>
            {scope && (
              <X
                size={11}
                style={{ color: "var(--tm)" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setScope(null);
                }}
              />
            )}
            <ChevronDown size={12} style={{ color: "var(--tm)" }} />
          </button>
          {scopeOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: Z.popoverBackdrop,
                marginTop: 4,
                background: "var(--bc)",
                border: "1px solid var(--br)",
                borderRadius: RAD.r6,
                boxShadow: "0 6px 20px rgba(0,0,0,.7)",
                overflow: "hidden",
              }}
            >
              <input
                autoFocus
                className="field-input"
                style={{
                  margin: 4,
                  width: "calc(100% - 8px)",
                  boxSizing: "border-box",
                  fontSize: FS.s11,
                }}
                placeholder={t("invoice.scope.search_placeholder")}
                value={scopeQ}
                onChange={(e) => setScopeQ(e.target.value)}
              />
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                <div
                  className="menu-item"
                  style={{
                    fontWeight: !scope ? 700 : 400,
                    color: !scope ? "var(--ac)" : "var(--tp)",
                  }}
                  onMouseDown={() => {
                    setScope(null);
                    setScopeOpen(false);
                    setScopeQ("");
                  }}
                >
                  {t("invoice.scope.all_cases")}
                </div>
                {scopeFiltered.length === 0 && (
                  <div
                    style={{
                      padding: "6px 10px",
                      fontSize: FS.s10_5,
                      color: "var(--tm)",
                      fontStyle: "italic",
                    }}
                  >
                    {t("invoice.scope.no_matches")}
                  </div>
                )}
                {scopeFiltered.map((o) => {
                  const isSel =
                    scope && scope.type === o.type && scope.id === o.id;
                  return (
                    <div
                      key={`${o.type}:${o.id}`}
                      className="menu-item"
                      style={{
                        fontWeight: isSel ? 700 : 400,
                        color: isSel ? "var(--ac)" : "var(--tp)",
                      }}
                      onMouseDown={() => {
                        setScope({ type: o.type, id: o.id });
                        setScopeOpen(false);
                        setScopeQ("");
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: FS.s10,
                          color: "var(--ts)",
                          marginRight: 6,
                        }}
                      >
                        {o.ref || "—"}
                      </span>
                      <span>{o.title}</span>
                      {o.type === "cc" && (
                        <span
                          style={{
                            fontSize: FS.s8,
                            color: "var(--pp)",
                            marginLeft: 6,
                          }}
                        >
                          {t("invoice.scope.collective")}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {scope && scopedCase && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ padding: "4px 10px", fontSize: FS.s10 }}
            onClick={() => nav(scope.type, scope.id)}
          >
            {t("invoice.btn.open_case")}
          </button>
        )}
      </div>

      {/* SECTION A: Time Entries (only when a case is selected) */}
      {scope && scopedCase && (
        <>
          <CaseTimeEntriesSection
            kind={scope.type === "cc" ? "cc" : "case"}
            cs={scopedCase}
            co={co}
            currentUser={currentUser}
            /* the resolver unwraps a settings object; this host holds no
               resolved axis and periodBounds accepts either shape */
            schedAxis={firmSettings}
            te={te}
            sTe={sTe}
            s={s}
            SK={SK}
            ca={scope.type === "ca" ? ca : []}
            cc={scope.type === "cc" ? cc : []}
            inv={inv}
            nid={nid}
            canSeeCounters={canSeeCards}
            /* 158 §3 [RULED] — and ONLY here. The strip is stuck to the
               bottom of the Invoices section whether or not the block is
               unlocked: nothing hides behind that lock on this screen and the
               figures are what the screen is for. CV, CCV and Dashboard do
               not pass this and keep 151C's unlock gate untouched. */
            countersAlwaysVisible
            tkTypes={tkTypes}
            canTerms={true}
            /* 150 §5 (e) — the add button follows the edit key here too; it
               was canEditTime={true} with 'the rights alignment is §5's act'
               written beside it, and this is that act. */
            canEditTime={_perms.can(
              "caseView.timeEntries.edit",
              scope.type === "cc" ? { ccId: scope.id } : { caseId: scope.id },
            )}
            /* 150 §5.3 — the markBilled key's own authority (admin · RP ·
               live grant), replacing admin-only: an RP marking on CV had no
               reason to lose the strip on the same matter's invoicing
               surface. */
            canMarkBilled={_perms.billingDelegate(
              scope.type === "cc" ? { ccId: scope.id } : { caseId: scope.id },
            )}
            canImportExport={_perms.can(
              "caseView.timeEntries.importExport",
              scope.type === "cc" ? { ccId: scope.id } : { caseId: scope.id },
            )}
            canPillColumn={_perms.can(
              "caseView.timeEntries.pillColumn",
              scope.type === "cc" ? { ccId: scope.id } : { caseId: scope.id },
            )}
            canBulkEdit={canBulkEditTe}
            onBulkEdit={(ids: number[], onDone: (r: TeBulkUpdateResult) => void) =>
              om && om("teBulk", { _teIds: ids, _onDone: onDone })
            }
            canBulkDelete={canBulkDeleteTe}
            confirmAndUndo={confirmAndUndo}
            onDoubles={handleDoubles}
            /* 151C §1 — the Invoicing surface's FIFTH row: Create pro forma
               far left, the dedup pair far right, unlocked only. */
            proFormaRow={true}
            /* 151C §2 (iii) — the pro forma list follows the entries inside
               the block (scroll window when unlocked, plain flow locked). */
            afterTable={invListBlock}
            _roleRows={_roleRows}
            openTimeModal={(k: string, p: Record<string, unknown>) => om && om(k, p)}
            onDelete={(e: any) => {
              /* 157 §2 — this view already owns a useConfirmAndUndo instance
                 and renders its dialog at :1477; no context needed here. */
              confirmAndUndo({
                // 161C §3
                title: t("confirm.delete_time_entry_title", {
                  fallback: "Delete the time entry?",
                }),
                message: t("invoice.te.confirm_delete"),
                confirmLabel: t("common.delete", { fallback: "Delete" }),
                onConfirm: () => {
                  s(SK.te, (te || []).filter((t2: any) => t2.id !== e.id), sTe);
                  toast(t("common.deleted"));
                },
              });
            }}
            onProForma={(p: any) =>
              /* 161 §4 — the WIDENED payload: the activities button sends the
                 bare id array, the retainer button an { entryIds: [], docKind }
                 object; one callback carries both shapes to the same modal. */
              onCreateProForma &&
              onCreateProForma({ ...(Array.isArray(p) ? { entryIds: p } : p), csId: scope.id, csType: scope.type })
            }
            t={t}
            toast={toast}
          />
        </>
      )}

      {!(scope && scopedCase) && invListBlock}
      {/* [W] — the firm's seven, pinned at the bottom exactly as under a
          matter; round 8 [AK]: the pin is App's footer slot (CountersStrip
          portals into it), the list scrolling above. */}
      {firmHost && firmFigures && (
        <CountersStrip host="firm" figures={firmFigures} t={t as TFn} />
      )}


      {/* Phase 35 Stage-3 — the credit modal is removed (replaced by the
          commercial-deduction field in the create modal). */}

    {doubles && (
      <ModalLayout
        onClose={() => setDoubles(null)}
        closeOnOverlay
        stopShellClicks
        overlayStyle={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        /* 166 §C(2) — the third of them, and the `92%` is a percentage of the
           overlay rather than a vw, which reaches the same place: the inert
           `maxWidth: 660` becomes the width. */
        width={660}
        maxHeight="80vh"
        shellStyle={{ overflow: "auto", padding: 16 }}
        rawBody
        headerContent={
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: FS.s13 }}>
              {doubles.mode === "in-case"
                ? t("te.doubles.in_case", { fallback: "Doubles in this case" })
                : t("te.doubles.cross_case", { fallback: "Doubles with other cases" })}
            </h3>
            <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "2px 7px" }} onClick={() => setDoubles(null)}>
              ✕
            </button>
          </div>
        }
        footer={null}
      >
          {!doubles.rows || doubles.rows.length === 0 ? (
            <p style={{ fontSize: FS.s12, color: "var(--tm)" }}>{t("te.doubles.none", { fallback: "No doubles found." })}</p>
          ) : (
            <Table style={{ width: "100%", fontSize: FS.s11 }}>
              <thead>
                <tr>
                  <th>{t("common.date", { fallback: "Date" })}</th>
                  <th>{t("te.col.who", { fallback: "Who" })}</th>
                  {doubles.mode === "cross-case" && <th>{t("te.doubles.matter", { fallback: "Matter" })}</th>}
                  <th>{t("te.col.billable", { fallback: "Billable" })}</th>
                  <th>{t("te.col.desc", { fallback: "Description" })}</th>
                </tr>
              </thead>
              <tbody>
                {doubles.rows.map((r: any) => {
                  const c = (co || []).find((x: any) => x.id === (r.coId ?? r.collaboratorId));
                  return (
                    <tr key={r.id}>
                      <td className="mono">{fd(r.date)}</td>
                      <td>{c ? pname(c) : "—"}</td>
                      {doubles.mode === "cross-case" && <td>{r.caseTitle || r.caseRef || "—"}</td>}
                      <td>{r.invoiceId ? t("te.mark.invoiced", { fallback: "Invoiced" }) : r.mark || r.billabilityMark || t("te.mark.none", { fallback: "unmarked" })}</td>
                      <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.desc || r.description || ""}>
                        {r.desc || r.description || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
      </ModalLayout>
    )}
    {/* 144 §5 — the shared confirm/undo surfaces, the CaseView mounts. */}
    <ConfirmDeleteDialog dlg={confirmDlg} onCancel={cancelConfirm} />
    <UndoToast toast={undoToast} onUndo={runUndo} />
    </>
  );
};

export default InvoiceView;
